import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem } from "@/features/common/components/dataview/GalleryItem";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { SearchBar } from "@/features/common/components/SearchBar";
import { SearchResults } from "@/features/common/components/SearchResults";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { useProductsSearch, type SearchProductsResponseModel } from "@/features/products/api/useProductsSearch";
import { ProductView } from "@/features/products/components/ProductView";
import { useDebouncedState } from "@/lib/common/useDebouncedState";
import { useQueryParams } from "@/lib/common/useQueryParams";
import { useTitle } from "@/lib/common/useTitle";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useSearchParams } from "react-router";

export const PageBrowse: FC = () => {
	useTitle("Browse");

	const queryParams = useQueryParams();
	const queryFromURL = useMemo(() => queryParams.get("q") || "", [queryParams]);
	const [, setSearchParams] = useSearchParams();
	const [debouncedSearchQuery, searchQuery, setSearchQuery] = useDebouncedState(queryFromURL, 500);

	useEffect(() => {
		setSearchParams({ q: debouncedSearchQuery });
	}, [debouncedSearchQuery]);

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Browse" }
	], []);

	return (
		<>
			<HeaderBar title="Browse" description="General product search and information" breadcrumb={breadcrumb} />

			<SearchBar value={searchQuery} onChange={setSearchQuery} />

			<PageBrowseInternal searchQuery={searchQuery ? debouncedSearchQuery : "s=iar"} />
		</>
	);
};

interface PageBrowseInternalProps {
	searchQuery: string;
}

const PageBrowseInternal: FC<PageBrowseInternalProps> = props => {
	const {
		searchQuery
	} = props;

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
	const products = useProductsSearch(searchQuery);
	const additionalProps = useMemo(() => ({
		onSelectProduct: setSelectedProductId
	}), [setSelectedProductId]);

	return (
		<SplitScreen fixed visible={!!selectedProductId} onVisibleChange={visible => !visible && setSelectedProductId(null)}>
			<SplitScreenLeftPanel ref={scrollRef}>
				<SearchResults searchQuery={searchQuery} forQuery={products}>
					<DataView
						galleryMinWidth={208}
						forQuery={products}
						getKey={x => x.productId}
						galleryComponent={PageBrowseDataViewGalleryItem}
						additionalProps={additionalProps}
						scrollRef={scrollRef} />
				</SearchResults>
			</SplitScreenLeftPanel>
			<SplitScreenRightPanel>
				{
					selectedProductId &&
					<ProductView productId={selectedProductId} className="min-h-full" />
				}
			</SplitScreenRightPanel>
		</SplitScreen>
	);
};

interface PageBrowseDataViewGalleryItemProps extends SearchProductsResponseModel {
	onSelectProduct: (productId: string) => void;
}

const PageBrowseDataViewGalleryItem: FC<PageBrowseDataViewGalleryItemProps> = props => {
	const {
		productId,
		name,
		imageUrl,
		number,
		onSelectProduct
	} = props;

	const onClick = useCallback(() => onSelectProduct(productId), [onSelectProduct, productId]);

	return <GalleryItem title={name} description={number ?? undefined} imageUrl={imageUrl} onClick={onClick} />;
};
